import moment from 'moment';
import { ICellRendererParams } from '@ag-grid-community/core';
import { NavigationExtras } from '@angular/router';

import {
	QualityIssue, DECISION_TR, CLAIM_TYPE_TR,
	CLOSED_STATUS_LIST, ISSUE_TYPE_TR
} from '../../../../claim/models/quality-issue';
import { TranslationService } from '../../../services/translation.service';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import {
	CommonColDef,
	DateColDef,
	LinkColDef,
	IconLinkColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterOrderNumberFilterComponent } from '../../filter-type/datacenter-order-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { formatNumber, parseNumberFromText } from '../../../utils/number.format';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';

const documentsExtras: NavigationExtras = {
	fragment: 'documents',
};

const linkToIssueCardCellRendererParams = {
	routerLink(params: ICellRendererParams<QualityIssue, unknown>) {
		const issue = params.data;
		return issue
			? {
				commands: [
					'/quality-issues',
					'card',
					issue.id,
				]
			}
			: undefined;
	}
};

const linkToIssueCardDocumentCellRendererParams = {
	routerLink(params: ICellRendererParams<QualityIssue, unknown>) {
		const issue = params.data;
		return issue?.creditNoteDate
			? {
				commands: [
					'/quality-issues',
					'card',
					issue.id,
				],
				extras: documentsExtras
			}
			: undefined;
	}
};

const linkToOrderCardCellRendererParams = {
	routerLink(params: ICellRendererParams<QualityIssue, unknown>) {
		const issue = params.data;
		return issue?.order
			? { commands: [
				'/order-tracking',
				'order',
				'card',
				issue.supplierOrderNumber,
				issue.supplierOrderItemNumber
			] }
			: undefined;
	}
};

type QualityIssueAndDisplayer = (QualityIssue & {
	sOrderRef: SORefDisplay;
});

class SORefDisplay {
	orderSearched: boolean;
	orderRef: string;

	constructor(issue?: QualityIssue, orderSearched = false) {
		this.orderSearched = orderSearched;
		this.orderRef = issue?.supplierOrderNumber && issue.supplierOrderItemNumber
			? `${ issue.supplierOrderNumber }/${ issue.supplierOrderItemNumber }`
			: '';
	}

	toString() {
		return this.orderRef;
	}
}

export class QualityDatacenterColumnDefinition {
	readonly QUALITY_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<QualityIssueAndDisplayer>>;

	constructor(private readonly translationService: TranslationService) {
		this.QUALITY_DATA_CENTER_DEFAULT_CONFIGURATION = new Map<string, CommonColDef<QualityIssueAndDisplayer>>([
			[
				'customerReference',
				new LinkColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('claim.placeholder.reference.number'),
					colId: 'customerReference',
					field: 'customerReference',
					cellRendererParams: linkToIssueCardCellRendererParams,
					filter: DatacenterTextFilterComponent,
					minWidth: 120,
					width: 150,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'supplierIssueNumber',
				new LinkColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.supplierissuenumber'),
					colId: 'supplierIssueNumber',
					field: 'supplierIssueNumber',
					cellRendererParams: linkToIssueCardCellRendererParams,
					filter: DatacenterTextFilterComponent,
					minWidth: 80,
					width: 90,
					maxWidth: 130,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'issueType',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('claim.request.type'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.issueType
							? this.translationService.getTR(this.getIssueTypeTr(issue.issueType))
							: '';
					},
					colId: 'issueType',
					fields: ['issueType'],
					minWidth: 60,
					maxWidth: 94,
					width: 94,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'claimType',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('claim.type.filter.label'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.claimType
							? this.translationService.getTR(this.getClaimTypeTr(issue.claimType))
							: '';
					},
					filter: DatacenterDropdownFilterComponent,
					colId: 'claimType',
					fields: ['claimType'],
					minWidth: 94,
					width: 94,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'creationDate',
				new DateColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('order.creation.date'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.creationDate
							? moment(issue.creationDate).format('YYYY-MM-DD')
							: '';
					},
					colId: 'creationDate',
					field: 'creationDate',
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					minWidth: 94,
					maxWidth: 94,
					width: 94,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					columnType: ColumnType.DATE_COLUMN,
				})
			],
			[
				'soldTo',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('order.customer'),
					field: 'soldToDescription',
					filter: DatacenterTextFilterComponent,
					minWidth: 120,
					width: 120,
					colId: 'soldTo',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'shipTo',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('datacenter-filter.ship-to'),
					field: 'shipToDescription',
					colId: 'shipTo',
					filter: DatacenterTextFilterComponent,
					minWidth: 120,
					width: 120,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'comment',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('claim.comment'),
					colId: 'comment',
					field: 'comment',
					minWidth: 150,
					filter: DatacenterTextFilterComponent,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'customerOrderNumber',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('order.customer.order.number'),
					colId: 'customerOrderNumber',
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.customerOrderNumber && issue.customerOrderItemNumber
							? `${ issue.customerOrderNumber }/${ issue.customerOrderItemNumber }`
							: '';
					},
					fields: ['customerOrderNumber', 'customerOrderItemNumber'],
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					minWidth: 120,
					width: 150,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'supplierOrderNumber',
				new LinkColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('order.supplier.order.number'),
					colId: 'supplierOrderNumber',
					valueGetter: params => {
						const issue = params.data;
						return issue ? issue.sOrderRef : '';
					},
					fields: ['supplierOrderNumber', 'supplierOrderItemNumber'],
					minWidth: 120,
					width: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
				})
			],
			[
				'status',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('claim.current.status'),
					colId: 'status',
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						if (issue?.status === undefined) {
							return undefined;
						}
						const threeYearsAgo = moment().subtract(3, 'year');
						const claimCloseDate = moment(issue.closedDate);
						const isArchived = threeYearsAgo.isAfter(claimCloseDate);
						return CLOSED_STATUS_LIST.includes(issue.status) && isArchived
							? this.translationService.getTR('claim.archived')
							: this.translationService.getTR(DECISION_TR[issue.status]);
					},
					fields: ['status'],
					minWidth: 100,
					maxWidth: 160,
					suppressHeaderMenuButton: true,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'docLink',
				new IconLinkColDef<QualityIssueAndDisplayer>({
					headerClass: ['copo-column-hide'],
					colId: 'docLink',
					headerName: this.translationService.getTR('datacenter.documents'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const quality = <QualityIssue> params.data;
						return quality.supplierOrderNumber;
					},
					fields: ['supplierOrderNumber', 'creditNoteDate'],
					cellRendererParams: {
						icon: 'table_document',
						...linkToIssueCardDocumentCellRendererParams
					}
				})
			],
			[
				'closedDate',
				new DateColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.closingdate'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.closedDate
							? moment(issue.closedDate).format('YYYY-MM-DD')
							: '';
					},
					colId: 'closedDate',
					field: 'closedDate',
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					minWidth: 80,
					maxWidth: 80,
					width: 80,
					columnType: ColumnType.DATE_COLUMN,
				})
			],
			[
				'creditNoteDate',
				new DateColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.creditnotedate'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.creditNoteDate
							? moment(issue.creditNoteDate).format('YYYY-MM-DD')
							: '';
					},
					colId: 'creditNoteDate',
					field: 'creditNoteDate',
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					minWidth: 80,
					maxWidth: 80,
					width: 80,
					columnType: ColumnType.DATE_COLUMN,
				})
			],
			[
				'customerDefect',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.customer.defect'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.customerDefectDetailed && issue.customerDefectDetailedDescription
							? issue.customerDefectDetailedDescription : '';
					},
					colId: 'customerDefect',
					field: 'customerDefectDetailed',
					minWidth: 150,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'batchReferenceNumber',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('datacenter-filter.batch-reference'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						let bachtnumbers = '';
						if (issue) {
							bachtnumbers = issue.batches.map(item => item.batchReference).join(',');
						}
						return bachtnumbers;
					},
					colId: 'batchReferenceNumber',
					fields: ['batches.batchReference'],
					minWidth: 100,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'acceptedQuantity',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.acceptedquantity'),
					colId: 'acceptedQuantity',
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.qtyAccepted
							? `${ issue.qtyAccepted } t` : '';
					},
					field: 'qtyAccepted',
					minWidth: 100,
					suppressHeaderMenuButton: false,
					sortable: true,
					filter: DatacenterNumberFilterComponent,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'claimQuantity',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.calimed.quantity'),
					colId: 'claimQuantity',
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.qtyRequested
							? `${ issue.qtyRequested } t` : '';
					},
					field: 'qtyRequested',
					minWidth: 100,
					suppressHeaderMenuButton: false,
					sortable: true,
					columnType: ColumnType.NUMBER_COLUMN,
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'weight-with-zero' },
				})
			],
			[
				'requestedTotalAmount',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.requested.total.amount'),
					colId: 'requestedTotalAmount',
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.amountRequested && issue.currencyRequested
							? formatNumber(issue.amountRequested, 'amount', issue.currencyRequested) : '';
					},
					fields: ['amountRequested', 'currencyRequested'],
					minWidth: 100,
					suppressHeaderMenuButton: false,
					sortable: true,
					filter: DatacenterTextFilterComponent,
					columnType: ColumnType.TEXT_COLUMN,
					cellRendererParams: { numericFormat: 'weight-with-zero' },
				})
			],
			[
				'totalAmountAccepted',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.total.amount.accepted'),
					colId: 'totalAmountAccepted',
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						if (issue?.amountAccepted && issue.currencyAccepted) {
							let acceptedAmt = parseNumberFromText(issue.amountAccepted);
							acceptedAmt = acceptedAmt !== undefined ? Math.abs(acceptedAmt) : acceptedAmt;
							return formatNumber(acceptedAmt, 'amount', issue.currencyAccepted);
						}
						return '';
					},
					fields: ['amountAccepted', 'currencyAccepted'],
					minWidth: 100,
					suppressHeaderMenuButton: false,
					sortable: false,
					filter: DatacenterTextFilterComponent,
					columnType: ColumnType.TEXT_COLUMN,
					cellRendererParams: { numericFormat: 'weight-with-zero' },
				})
			],
			[
				'origin',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('issue.origin'),
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						return issue?.origin
							? this.translationService.getIssueOriginTr(issue.origin) : '';
					},
					colId: 'origin',
					field: 'origin',
					minWidth: 80,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'mill',
				new CommonColDef<QualityIssueAndDisplayer>({
					headerName: this.translationService.getTR('datacenter-filter.mill'),
					field: 'mill',
					colId: 'mill',
					valueGetter: params => {
						const issue = <QualityIssue | undefined> params.data;
						if (issue?.order) {
							return issue.order.millDescription;
						}
						return '';
					},
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			]
		]);
	}

	getIssueTypeTr(type: string) {
		if (type === 'warning') {
			return ISSUE_TYPE_TR.warning;
		}
		return ISSUE_TYPE_TR.claim;
	}

	getClaimTypeTr(type: string) {
		if (type === 'ZL') {
			return CLAIM_TYPE_TR.ZL;
		}
		return CLAIM_TYPE_TR.ZR;
	}
}

export const DEFAULT_VIEW_QUALITY_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'customerReference',
			pinned: 'left'
		},
		{
			colId: 'supplierIssueNumber',
			pinned: 'left'
		},
		{
			colId: 'issueType',
			pinned: 'left'
		},
		{
			colId: 'creationDate'
		},
		{
			colId: 'soldTo'
		},
		{
			colId: 'shipTo'
		},
		{
			colId: 'comment'
		},
		{
			colId: 'customerOrderNumber'
		},
		{
			colId: 'supplierOrderNumber'
		},
		{
			colId: 'status'
		},
		{
			colId: 'docLink'
		}
	],
	sortModel: [
		{
			colId: 'creationDate',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'quality',
	isDefault: true,
	name: 'Default',
	id: -99
};
